"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const standards = [{
  name: "CRM Integration",
  body: "We architect unified communication pipelines that bridge the gap between client inquiry and field execution. By centralizing lead data and automated follow-ups, we ensure no opportunity is lost in the transition from digital interest to physical site visit."
}, {
  name: "FSM Infrastructure",
  body: "Our field service management configurations prioritize operational visibility. We build custom tracking systems that allow you to monitor crew progress, manage site assets, and document project milestones in real-time, ensuring your backend matches the pace of your field work."
}, {
  name: "Automated Bookkeeping",
  body: "We design financial workflows that eliminate manual entry. By integrating your invoicing, payroll, and expense tracking into a single, automated ledger, we provide the clarity needed to make high-level business decisions without the burden of administrative overhead."
}, {
  name: "Scheduling & Dispatch",
  body: "We implement intelligent scheduling engines that optimize your team's time. Our systems account for travel, material lead times, and crew availability, creating a predictable, scalable rhythm for your daily operations that scales as your business grows."
}];
export default function Framework() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  return <section data-section-id="10631" id="our-operational-framework" className="dark bg-background text-foreground pt-40 md:pt-52 pb-32">
      <div className="mx-auto w-full max-w-[1440px] px-5 md:px-8 lg:px-12">
        <div className="grid grid-cols-4 md:grid-cols-12 gap-4 md:gap-6">
          {}
          <div className="col-span-4 md:col-span-5">
            <div className="md:sticky md:top-24">
              <h1 ref={headlineRef} className="font-default text-foreground" style={{
              fontSize: "clamp(2.75rem, 5.2vw, 5.25rem)",
              fontWeight: 500,
              letterSpacing: "-0.025em",
              lineHeight: 0.98,
              clipPath: headlineVisible ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
              transition: "clip-path 600ms cubic-bezier(0.7, 0, 0.2, 1)"
            }}>
                Operational
                <br />
                framework
              </h1>

              <div className="mt-12">
                <span className="font-default text-primary" style={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                letterSpacing: "0.08em",
                lineHeight: 1.2,
                textTransform: "uppercase"
              }}>
                  Architectural Pillars
                </span>

                <div className="mt-4">
                  <Link to="/ServicesPage" className="group inline-flex items-center gap-2 font-default text-foreground" style={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  letterSpacing: "-0.005em"
                }}>
                    <span className="relative">
                      View our technical capabilities
                      <span className="absolute left-0 -bottom-1 h-px w-0 group-hover:w-full" style={{
                      backgroundColor: "var(--chart-1)",
                      transition: "width 220ms ease-out"
                    }} />
                    </span>
                    <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.25} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="col-span-4 md:col-span-7 mt-16 md:mt-0">
            <ul className="flex flex-col">
              {standards.map((item, i) => <StandardRow key={i} index={i} name={item.name} body={item.body} />)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
function StandardRow({
  index,
  name,
  body
}: {
  index: number;
  name: string;
  body: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.15
  });
  return <li data-index={index} ref={ref} className="border-t border-border py-10 md:py-10" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(12px)",
    transition: `opacity 480ms cubic-bezier(0.2, 0.6, 0.2, 1) ${index * 100}ms, transform 480ms cubic-bezier(0.2, 0.6, 0.2, 1) ${index * 100}ms`
  }}>
      <div className="grid grid-cols-1 md:grid-cols-7 md:gap-6">
        <h3 className="font-default text-foreground md:col-span-4" style={{
        fontSize: "clamp(1.25rem, 1.6vw, 1.625rem)",
        fontWeight: 500,
        letterSpacing: "-0.012em",
        lineHeight: 1.15
      }}>
          {name}
        </h3>
        <p className="mt-4 md:mt-0 md:col-span-7 font-default" style={{
        color: "var(--muted-foreground)",
        fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
        fontWeight: 400,
        letterSpacing: "-0.005em",
        lineHeight: 1.55,
        textWrap: "pretty"
      }}>
          {body}
        </p>
      </div>
    </li>;
}